import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  Printer, 
  FileText, 
  Check, 
  Upload, 
  Eye, 
  Settings2, 
  QrCode, 
  Image as ImageIcon,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import BillPreview from '../retail/BillPreview';
import { BillData, BillBlock } from '@/src/lib/billRenderer';

export function ServicesPrintSettingsView() {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();
  const stampInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedPrinterSize, setSelectedPrinterSize] = useState<string>('A4');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  
  const [businessInfo, setBusinessInfo] = useState({
    businessName: 'Apex Services & Care',
    branchName: 'Main Workshop & Desk',
    address: 'Plot 42, Service Industrial Area, Mumbai, Maharashtra 400001',
    phone: '+91 98765 43210'
  });

  const [brandingSettings, setBrandingSettings] = useState<any>({
    show_branch_name: true,
    show_branch_address: true,
    bill_number_code_type: 'qr',
    show_upi_qr_on_credit: true,
    credit_qr_provider: 'razorpay',
    stamp_url: null
  });

  const loadSettings = async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch available bill templates
      const { data: tData } = await supabase
        .from('bill_templates')
        .select('*')
        .eq('is_default', true)
        .order('printer_size');
      setTemplates(tData || []);

      // 2. Fetch tenant bill format selection for services / sale_invoice
      const { data: selData } = await supabase
        .from('bill_format_selection')
        .select('printer_size, template_id')
        .eq('tenant_id', currentTenantId)
        .eq('channel', 'service_bill')
        .maybeSingle();

      if (selData) {
        if (selData.printer_size) setSelectedPrinterSize(selData.printer_size);
        if (selData.template_id) setSelectedTemplateId(selData.template_id);
      }

      // 3. Fetch tenant & branch info
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('business_name')
        .eq('id', currentTenantId)
        .maybeSingle();

      const { data: branchData } = await supabase
        .from('branches')
        .select('branch_name, address, city, state, pincode, phone')
        .eq('tenant_id', currentTenantId)
        .order('is_main_branch', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (tenantData?.business_name) {
        setBusinessInfo(prev => ({
          ...prev,
          businessName: tenantData.business_name,
          branchName: branchData?.branch_name || prev.branchName,
          address: [branchData?.address, branchData?.city, branchData?.state, branchData?.pincode].filter(Boolean).join(', ') || prev.address,
          phone: branchData?.phone || prev.phone
        }));
      }

      // 4. Fetch print branding settings
      const { data: brandingData } = await supabase
        .from('print_branding_settings')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .maybeSingle();

      if (brandingData) {
        setBrandingSettings(brandingData);
      }
    } catch (err: any) {
      console.error('Failed to load print settings:', err);
      toast.error('Error loading print settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [currentTenantId]);

  const handleToggleBranding = async (
    key: 'show_branch_name' | 'show_branch_address' | 'bill_number_code_type' | 'show_upi_qr_on_credit' | 'credit_qr_provider',
    value: any
  ) => {
    if (!supabase || !currentTenantId) return;
    const newSettings = { ...brandingSettings, tenant_id: currentTenantId, [key]: value };
    setBrandingSettings(newSettings);

    const { error } = await supabase
      .from('print_branding_settings')
      .upsert(newSettings, { onConflict: 'tenant_id' });

    if (error) {
      toast.error('Failed to save branding: ' + error.message);
    } else {
      toast.success('Branding updated');
    }
  };

  const handleSelectTemplate = async (printerSize: string, templateId: string) => {
    setSelectedPrinterSize(printerSize);
    setSelectedTemplateId(templateId);

    if (!supabase || !currentTenantId) return;
    const { error } = await supabase
      .from('bill_format_selection')
      .upsert({
        tenant_id: currentTenantId,
        channel: 'service_bill',
        printer_size: printerSize,
        template_id: templateId,
        updated_at: new Date().toISOString()
      }, { onConflict: 'tenant_id,channel' });

    if (error) {
      toast.error('Failed to save template selection: ' + error.message);
    } else {
      toast.success('Bill template updated');
    }
  };

  const handleStampUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentTenantId || !supabase) return;

    toast.info('Uploading stamp/signature...');
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `${currentTenantId}/stamp_${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('product-images').upload(path, file);

      if (uploadErr) {
        toast.error('Upload failed: ' + uploadErr.message);
        return;
      }

      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(path);
      const newSettings = { ...brandingSettings, tenant_id: currentTenantId, stamp_url: urlData.publicUrl };

      const { error: saveErr } = await supabase
        .from('print_branding_settings')
        .upsert(newSettings, { onConflict: 'tenant_id' });

      if (saveErr) {
        toast.error('Failed to save stamp: ' + saveErr.message);
        return;
      }

      setBrandingSettings(newSettings);
      toast.success('Stamp uploaded and saved');
    } catch (err: any) {
      toast.error(err.message || 'Stamp upload failed');
    } finally {
      if (stampInputRef.current) stampInputRef.current.value = '';
    }
  };

  // Sample Services Bill Data for Live Preview
  const sampleServicesBillData: BillData = {
    business: {
      name: businessInfo.businessName,
      branch_name: businessInfo.branchName,
      address: businessInfo.address,
      phone: businessInfo.phone,
      stamp_url: brandingSettings.stamp_url,
      show_branch_name: brandingSettings.show_branch_name !== false,
      show_branch_address: brandingSettings.show_branch_address !== false
    },
    customer: {
      name: 'Aditya Sharma',
      phone: '+91 98200 12345',
      address: 'A-302, Green Meadows, Andheri West, Mumbai',
      gstin: '27AABCU9603R1ZM'
    },
    customer_info_label: 'Bill To / Customer Details',
    meta: {
      label: 'SERVICE TAX INVOICE',
      number: 'SRV-2026-0042',
      date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      extra: [
        { label: 'Booking Ref', value: 'BK-9921' },
        { label: 'Payment Status', value: 'PARTIALLY PAID' },
        { label: 'Advance Paid', value: '₹500.00' },
        { label: 'Balance Due', value: '₹1,152.00' }
      ]
    },
    items: [
      {
        name: 'AC Deep Foam Cleaning & Service',
        qty: 1,
        unit: 'Job',
        rate: 800,
        tax_percent: 18,
        amount: 800
      },
      {
        name: '[Material] Heavy Duty Compressor Capacitor 45uF',
        qty: 1,
        unit: 'pcs',
        rate: 600,
        tax_percent: 18,
        amount: 600
      }
    ],
    totals: {
      subtotal: 1400,
      discount: 0,
      tax_breakdown: [
        { label: 'CGST 9%', amount: 126 },
        { label: 'SGST 9%', amount: 126 }
      ],
      grand_total: 1652
    },
    footer: {
      signature_label: 'Authorized Signatory',
      stamp_url: brandingSettings.stamp_url
    },
    bill_number_code_type: brandingSettings.bill_number_code_type || 'none',
    upi_payment: brandingSettings.show_upi_qr_on_credit ? {
      upi_id: 'merchant@upi',
      payee_name: businessInfo.businessName,
      amount: 1152
    } : undefined
  };

  const currentSizeTemplates = templates.filter(t => t.printer_size === selectedPrinterSize);
  const activeTemplate = templates.find(t => t.id === selectedTemplateId) || currentSizeTemplates[0];

  const previewBlocks: BillBlock[] = activeTemplate?.blocks || [
    { type: 'header', order: 1, visible: true, config: {} },
    { type: 'customer_info', order: 2, visible: true, config: {} },
    { type: 'invoice_meta', order: 3, visible: true, config: {} },
    { type: 'item_table', order: 4, visible: true, config: { show_rate: true, show_tax: true } },
    { type: 'totals', order: 5, visible: true, config: { show_tax_breakdown: true } },
    { type: 'footer', order: 6, visible: true, config: {} }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <Printer className="text-indigo-600 dark:text-indigo-400" size={24} />
            <span>Bill Print & Branding Blueprint Settings</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Configure tenant-wide print styles, thermal roll sizes, branch details, stamps, and QR codes for Services bills
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadSettings}
          className="h-9 gap-1.5 self-start sm:self-auto text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          <span>Reload</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Configuration Controls (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Printer Size & Template Selection Card */}
          <Card className="border-slate-200 dark:border-slate-800 shadow-xs">
            <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-800">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
                <Settings2 size={18} className="text-primary" />
                <span>Printer Size & Template Style</span>
              </CardTitle>
              <CardDescription className="text-xs">
                Select your default physical paper format. The selected template layout is shared across your tenant.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">
                  Paper / Printer Size
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'A4', label: 'A4 Size', desc: 'Standard Full Sheet' },
                    { id: 'A4-Half', label: 'A4-Half / A5', desc: 'Compact Sheet' },
                    { id: '112mm', label: '112mm Thermal', desc: '4-inch Receipt' },
                    { id: '80mm', label: '80mm Thermal', desc: '3-inch POS Roll' },
                    { id: '58mm', label: '58mm Thermal', desc: '2-inch Compact' }
                  ].map(size => (
                    <button
                      key={size.id}
                      type="button"
                      onClick={() => {
                        setSelectedPrinterSize(size.id);
                        const match = templates.find(t => t.printer_size === size.id);
                        if (match) setSelectedTemplateId(match.id);
                      }}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        selectedPrinterSize === size.id
                          ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-950 dark:text-indigo-200 ring-1 ring-indigo-600/30'
                          : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold">{size.label}</span>
                        {selectedPrinterSize === size.id && (
                          <Check size={14} className="text-indigo-600 dark:text-indigo-400" />
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">{size.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Template Styles for Selected Size */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">
                  Layout Template Style ({selectedPrinterSize})
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {currentSizeTemplates.length === 0 ? (
                    <div className="col-span-2 p-3 text-center text-xs text-slate-400 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      Classic layout will be used for this paper size.
                    </div>
                  ) : (
                    currentSizeTemplates.map(t => (
                      <div
                        key={t.id}
                        className={`relative border-2 rounded-xl overflow-hidden cursor-pointer transition-all ${
                          (selectedTemplateId === t.id || (!selectedTemplateId && t.style_name === 'Classic'))
                            ? 'border-indigo-600 dark:border-indigo-500 shadow-xs'
                            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                        }`}
                        onClick={() => handleSelectTemplate(selectedPrinterSize, t.id)}
                      >
                        <div className="p-3 bg-white dark:bg-slate-900 flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{t.style_name}</span>
                            <span className="text-[10px] text-slate-400 block">{t.printer_size} template</span>
                          </div>
                          {(selectedTemplateId === t.id || (!selectedTemplateId && t.style_name === 'Classic')) && (
                            <div className="h-5 w-5 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tenant Branding & Print Elements Card */}
          <Card className="border-slate-200 dark:border-slate-800 shadow-xs">
            <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-800">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
                <Sparkles size={18} className="text-amber-500" />
                <span>Branding, Stamp & Header Elements</span>
              </CardTitle>
              <CardDescription className="text-xs">
                Shared tenant-level settings stored in print_branding_settings. Updates here immediately take effect on all bills.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Stamp Upload */}
              <div className="p-3.5 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {brandingSettings.stamp_url ? (
                    <img
                      src={brandingSettings.stamp_url}
                      alt="Stamp"
                      className="h-14 w-14 object-contain rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-1"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 bg-white dark:bg-slate-950">
                      <ImageIcon size={20} />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">Official Stamp / Signature</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Appears over the signatory box on your invoices</p>
                  </div>
                </div>

                <div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={stampInputRef}
                    className="hidden"
                    onChange={handleStampUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => stampInputRef.current?.click()}
                    className="h-8 text-xs gap-1.5"
                  >
                    <Upload size={12} />
                    <span>{brandingSettings.stamp_url ? 'Change Stamp' : 'Upload Stamp'}</span>
                  </Button>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3 pt-1">
                <label className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-pointer">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Show Branch Name on Bill Header
                  </span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded text-indigo-600 cursor-pointer"
                    checked={brandingSettings.show_branch_name !== false}
                    onChange={e => handleToggleBranding('show_branch_name', e.target.checked)}
                  />
                </label>

                <label className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-pointer">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Show Branch Address & Contact Details on Bill
                  </span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded text-indigo-600 cursor-pointer"
                    checked={brandingSettings.show_branch_address !== false}
                    onChange={e => handleToggleBranding('show_branch_address', e.target.checked)}
                  />
                </label>
              </div>

              {/* Bill Number Code Type */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">
                  Bill Number Code (Printed on Invoice Header)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {[
                    { id: 'none', label: 'None' },
                    { id: 'barcode', label: 'Barcode' },
                    { id: 'qr', label: 'QR Code' },
                    { id: 'both', label: 'Both' }
                  ].map(ct => (
                    <button
                      key={ct.id}
                      type="button"
                      onClick={() => handleToggleBranding('bill_number_code_type', ct.id)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        (brandingSettings.bill_number_code_type || 'none') === ct.id
                          ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-950 dark:text-indigo-200 font-bold'
                          : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {ct.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* UPI Payment QR on Credit Bills */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
                <label className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-pointer">
                  <div>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300 block">
                      Show UPI Payment QR on Invoices with Balance Due
                    </span>
                    <span className="text-[10px] text-slate-400">Allows customer to scan and pay remaining dues directly from the printed invoice</span>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded text-indigo-600 cursor-pointer"
                    checked={brandingSettings.show_upi_qr_on_credit === true}
                    onChange={e => handleToggleBranding('show_upi_qr_on_credit', e.target.checked)}
                  />
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Live Sample Services Bill Preview (5 cols) */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <Eye size={13} className="text-indigo-600 dark:text-indigo-400" />
              <span>Live Services Invoice Preview ({selectedPrinterSize})</span>
            </span>
            <span className="text-[10px] text-slate-400">Updates dynamically</span>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 p-2 sm:p-4 overflow-hidden shadow-inner">
            <div className="max-h-[720px] overflow-y-auto rounded-xl">
              <BillPreview
                blocks={previewBlocks}
                data={sampleServicesBillData}
                printerSize={selectedPrinterSize}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServicesPrintSettingsView;
